import React, {useState, useEffect, useRef} from 'react';
import {Alert, Compass, ContextMenu, Drawer, Navigation, ThreeDViewer} from '../../components';
import {Button, Tabs} from "antd";
import Modal from "../../components/Layout-Components/Editor-Layout/Modal/Modal";
import SiteLoadLoader from "../../components/Layout-Components/Editor-Layout/Loader/SiteLoadLoader";
import Toolbar from "../../components/Layout-Components/Editor-Layout/Toolbar/Toolbar";
import Actionbar from "../../components/Layout-Components/Editor-Layout/Actionbar/Actionbar";
import { PciFinalizeBridge } from "../../components/Layout-Components/Editor-Layout/Actionbar/PciFinalizeBridge";
import StreetView from "../../components/Layout-Components/Editor-Layout/StreetView/StreetView";
import {useViewerStore} from '../../store/store';
import Addressbar from "../../components/Layout-Components/Editor-Layout/Addressbar/Addressbar";
import MenubarPathBar from "../../components/Layout-Components/Editor-Layout/MenubarPathBar/MenubarPathBar";
import Sidebar from "../../components/Layout-Components/Editor-Layout/Sidebar/Sidebar";
import Menubar from "../../components/Layout-Components/Editor-Layout/Menubar/Menubar";
import {LoadingContext} from "../../store/loadingMessageStore";
import { useIdleTimer } from 'react-idle-timer';
import { useLocation, useNavigate } from 'react-router-dom';
import {WarningIcon} from "@phosphor-icons/react";
import {useCurrentUser} from "../../contexts/UserContext";
import { setPathname } from '../../helpers/storeHelpers';
import TotalSystemEfficiency from "../../components/Layout-Components/Editor-Layout/Popout-Window/Production-Calculation-Popout/Production-Calculation-Popout-Tabs/TotalSystemEfficiency";
import RoofInformationTable from "../../components/Layout-Components/Editor-Layout/Popout-Window/Production-Calculation-Popout/Production-Calculation-Popout-Tabs/RoofInformationTable";
import IrradienceCharts from "../../components/Layout-Components/Editor-Layout/Popout-Window/Production-Calculation-Popout/Production-Calculation-Popout-Tabs/IrradienceCharts";
import MonthlyProductionEstimate from "../../components/Layout-Components/Editor-Layout/Popout-Window/Production-Calculation-Popout/Production-Calculation-Popout-Tabs/MonthlyProductionEstimate";

type MobileBottomPanel = 'sidebar' | 'popout';

const getSidebarTabLabelFromPath = (pathname: string): string => {
    const pathLabelMap: Record<string, string> = {
        '/configure/building': 'Building',
        '/configure/roof': 'Roof',
        '/configure/obstruction': 'Obstruction',
        '/configure/tree': 'Tree',
        '/configure/modules': 'Modules',
        '/configure/solar-access': 'Shade Point',
        '/configure/irradiance': 'Irradiance',
        '/configure/price-calculation': 'EPC',
        '/configure/what-if': 'What If',
        '/configure/custom-image': 'Custom Imagery',
        '/configure/surface-draw': 'Ground Mount',
        '/configure/tape-measure': 'Tape Measure',
        '/configure/packing-slip': 'Packing Slip',
        '/configure/building/engineering-letter': 'Building',
        '/configure/building/engineering-affidavit': 'Building',
    };

    return pathLabelMap[pathname] ?? 'Sidebar';
};

const getDefaultPopoutTabLabelFromPath = (pathname: string): string | null => {
    // EPC mounts Tree Cart as its dedicated popout — never the Production fallback.
    if (pathname === '/configure/price-calculation') {
        return 'Tree Table';
    }

    // Routes that mount their own PopoutWindow (label comes from that popout).
    const pathsWithDedicatedPopout = new Set([
        '/configure/roof',
        '/configure/modules',
        '/configure/irradiance',
    ]);

    if (pathsWithDedicatedPopout.has(pathname)) {
        return null;
    }

    return 'Production';
};

const MobileProductionPanel: React.FC = () => {
    const tabItems = [
        {
            key: 'totalSystemEfficiencyTab',
            label: 'Total System Efficiency',
            children: <TotalSystemEfficiency popoutVersion={true}/>,
        },
        {
            key: 'roofEfficiencyTab',
            label: 'Roof Efficiency Table',
            children: <RoofInformationTable/>,
        },
        {
            key: 'irradianceTab',
            label: 'Irradiance',
            children: <IrradienceCharts/>,
        },
        {
            key: 'monthlyProductionEstimate',
            label: 'Monthly Production Estimate',
            children: <MonthlyProductionEstimate/>,
        },
    ];

    return (
        <div className="popup expand">
            <div className="top_bar">
                <b className="popup__header-title" title="Production">
                    Production
                </b>
            </div>
            <div className="popup__content">
                <Tabs defaultActiveKey="totalSystemEfficiencyTab" items={tabItems}/>
            </div>
        </div>
    );
};

const EditorPage: React.FC = () => {

    const location = useLocation();
    const {user} = useCurrentUser();
    const viewerAlert = useViewerStore(state => state.viewerAlert);
    const isDashboardOpen = useViewerStore((state) => Boolean(state.isDashboardOpen));
    const isViewportOnlyEmbed = useViewerStore((state) => Boolean(state.isViewportOnlyEmbed));
    const idleRedirectEnabled = useViewerStore((state) => Boolean(state.idleRedirectEnabled));
    const {hasCapability} = useCurrentUser();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [remainingTime, setRemainingTime] = useState(600); // Initialize with total idle limit in seconds
    const navigate = useNavigate();
    const startTimeRef = useRef<Date | null>(null);
    const intervalRef = useRef<number | null>(null);
    const [permanentIdle, setPermanentIdle] = useState(false);
    const [isMobileViewport, setIsMobileViewport] = useState(false);
    const [isPhoneBottomViewport, setIsPhoneBottomViewport] = useState(false);
    const [mobileBottomPanel, setMobileBottomPanel] = useState<MobileBottomPanel>('sidebar');
    const pathname = useViewerStore((state) => state.pathname);
    const mobileSidebarTabLabel = useViewerStore((state) => state.mobileSidebarTabLabel);
    const mobilePopoutTabLabel = useViewerStore((state) => state.mobilePopoutTabLabel);

    const isPhoneViewport = () => window.innerWidth <= 540;

    const isBottomSplitViewport = () =>
        isPhoneViewport() || window.matchMedia('(orientation: portrait) and (max-width: 1024px)').matches;

    const handleOnIdle = () => {
        if (!useViewerStore.getState().idleRedirectEnabled) return;
        setIsModalOpen(true);
        startTimeRef.current = new Date();
        setRemainingTime(300); // 5 minutes countdown in seconds
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setRemainingTime(600); // Reset total idle limit
        clearTimer();
        reset();
    };

    const { reset } = useIdleTimer({
        timeout: 600000, // 10 minutes in milliseconds
        onIdle: handleOnIdle,
        debounce: 500,
        crossTab: true,
        disabled: !idleRedirectEnabled,
        events: ['mousemove', 'keydown', 'mousedown', 'touchstart'], // Add events to reset the timer
    });

    const clearTimer = () => {
        if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null; // reset for safety
        }
    };

    const idleMessage = hasCapability('Sites_Table') ? 
    `You will be rerouted to the Sites table`
    : `You will need to refresh the page`;

    useEffect(() => {
        if (!idleRedirectEnabled || !isModalOpen) return undefined;
        intervalRef.current = window.setInterval(() => {
            if (!startTimeRef.current) return;
            const now = new Date();
            const elapsed = Math.floor((now.getTime() - startTimeRef.current.getTime()) / 1000);

            setRemainingTime(() => {
                const newRemainingTime = 300 - elapsed;
                if (newRemainingTime <= 0) {
                    if (hasCapability('Sites_Table')) {
                        navigate('/sites');
                        clearTimer();
                    } else {
                        setPermanentIdle(true);
                    }
                }
                return newRemainingTime;
            });
        }, 1000);

        return () => {
            clearTimer();
        };
    }, [idleRedirectEnabled, isModalOpen, navigate, hasCapability]);

    useEffect(() => {
        const isModalOpen = viewerAlert.show && viewerAlert.isModal && !isDashboardOpen;
        useViewerStore.setState({isModalOpenInInterface: isModalOpen});
    }, [viewerAlert, isDashboardOpen]);

    useEffect(() => {
        if (!isDashboardOpen) return;
        const { viewerAlert: activeAlert, sceneViewer } = useViewerStore.getState();
        if (activeAlert?.show) {
            sceneViewer?.removeViewerAlert?.();
        }
    }, [isDashboardOpen]);

    const viewerAlertModalClassName =
        viewerAlert.modalClassName ??
        (viewerAlert.title === 'Save Design' ? 'modal__content--save-design' : undefined);

    useEffect(() => {
        const loadingContext = new LoadingContext();
        loadingContext.setMessage({ message: 'Loading site…' });
        useViewerStore.setState({ initialLoadLoadingContextRef: loadingContext });
    }, []);

    useEffect(() => {
        if (location.pathname.startsWith('/configure/') && location.pathname !== '/configure') {
            setPathname(location.pathname);
        }
    }, [location.pathname]);

    useEffect(() => {
        sessionStorage.removeItem('sfidForRedirect');
    }, []);

    useEffect(() => {
        const syncViewportState = () => {
            const isPhone = isPhoneViewport();
            const bottomSplit = isBottomSplitViewport();
            setIsPhoneBottomViewport(isPhone);
            setIsMobileViewport(bottomSplit);
            if (!bottomSplit) {
                setMobileBottomPanel('sidebar');
            }
        };

        syncViewportState();
        window.addEventListener('resize', syncViewportState);
        return () => window.removeEventListener('resize', syncViewportState);
    }, []);

    useEffect(() => {
        if (isMobileViewport) {
            useViewerStore.setState({ isRightSidebarOpen: isPhoneBottomViewport ? mobileBottomPanel === 'popout' : true });
        }

        const body = document.body;
        body.classList.toggle('mobile-bottom-layout', isMobileViewport);
        body.classList.toggle('mobile-bottom-tablet-split', isMobileViewport && !isPhoneBottomViewport);
        body.classList.toggle('mobile-bottom-tab-sidebar', isMobileViewport && isPhoneBottomViewport && mobileBottomPanel === 'sidebar');
        body.classList.toggle('mobile-bottom-tab-popout', isMobileViewport && isPhoneBottomViewport && mobileBottomPanel === 'popout');

        return () => {
            body.classList.remove('mobile-bottom-layout', 'mobile-bottom-tablet-split', 'mobile-bottom-tab-sidebar', 'mobile-bottom-tab-popout');
        };
    }, [isMobileViewport, isPhoneBottomViewport, mobileBottomPanel]);

    useEffect(() => {
        const sidebarTabLabel = getSidebarTabLabelFromPath(pathname);
        const defaultPopoutTabLabel = getDefaultPopoutTabLabelFromPath(pathname);
        const currentState = useViewerStore.getState();
        const nextState: Partial<{
            mobileSidebarTabLabel: string;
            mobilePopoutTabLabel: string;
        }> = {};

        if (currentState.mobileSidebarTabLabel !== sidebarTabLabel) {
            nextState.mobileSidebarTabLabel = sidebarTabLabel;
        }

        if (defaultPopoutTabLabel && currentState.mobilePopoutTabLabel !== defaultPopoutTabLabel) {
            nextState.mobilePopoutTabLabel = defaultPopoutTabLabel;
        }

        if (Object.keys(nextState).length > 0) {
            useViewerStore.setState(nextState);
        }
    }, [pathname]);

    return (
        <div
            id={"editMainContainer"}
            className={`editor${isViewportOnlyEmbed ? ' editor--viewport-embed' : ''}`}
        >
            {!isViewportOnlyEmbed && idleRedirectEnabled ? (
                <div className="Idle Alert">
                    {(isModalOpen && !permanentIdle) &&
                        <Modal title="Idle Session" close={handleCloseModal}>
                            <div className="editor-idle-modal">
                            <p className="editor-idle-modal__title">This session has been idle.</p>
                            <p className="editor-idle-modal__message">
                                {idleMessage} in {Math.floor(remainingTime / 60)}:{(remainingTime % 60).toString().padStart(2, '0')} seconds due to inactivity.
                            </p>
                            <div className="editor-idle-modal__warning">
                                <Button
                                    type="text"
                                    icon={<WarningIcon size={18}/>}
                                    danger
                                    className="no-click"
                                >
                                    Note: Any unfinalized changes will be lost.
                                </Button>   
                            </div>
                            <div className={"modal-bottom-action-button-container"}>
                                <Button
                                    type="primary"
                                    size="middle"
                                    className="editor__control--finalize modal-confirm-button"
                                    onClick={handleCloseModal}
                                >
                                    I'm still here
                                </Button>
                            </div>
                            </div>
                        </Modal>
                    }
                    {(isModalOpen && permanentIdle) && //Do not reroute users to Sites table if they do not have access
                        <Modal title="Idle Session">
                            You have been idle for more than 15 minutes. Please refresh the page.
                        </Modal>
                    }
                </div>
            ) : null}

            {!isViewportOnlyEmbed ? (
                <>
                    <Menubar/>
                    <Drawer/>
                    <ContextMenu/>
                    <Actionbar/>
                    <Addressbar/>
                    <MenubarPathBar/>
                    {!isMobileViewport && <Sidebar/>}
                </>
            ) : null}
            <SiteLoadLoader/>
            {!isViewportOnlyEmbed ? <StreetView/> : null}
            <ThreeDViewer/>
            {isViewportOnlyEmbed ? <PciFinalizeBridge /> : null}

            {
                !isViewportOnlyEmbed && !isDashboardOpen && viewerAlert.show && viewerAlert.isModal === true &&
                <Modal
                    title={viewerAlert.title ?? 'Modal'}
                    contentClassName={viewerAlertModalClassName}
                    close={() => {
                        const sv = useViewerStore.getState().sceneViewer;
                        sv?.viewerAlert?.({ show: false });
                    }}
                >
                    {viewerAlert.message}
                </Modal>
            }
            {
                !isViewportOnlyEmbed && !isDashboardOpen && viewerAlert.show && viewerAlert.isModal === false &&
                <Alert
                    title={viewerAlert.title}
                    message={viewerAlert.message}
                    messageType={viewerAlert.messageType}
                    isCloseAfterDisplay={viewerAlert.isCloseAfterDisplay}
                    autoCloseSeconds={viewerAlert.autoCloseSeconds}
                    isModal={viewerAlert.isModal}
                    onClose={() => useViewerStore.setState({viewerAlert: {show: false}})}
                    show={viewerAlert.show}
                />
            }

            {!isViewportOnlyEmbed ? <Toolbar/> : null}
            {!isViewportOnlyEmbed && isMobileViewport && (
                <div className="mobile-bottom-panel">
                    {isPhoneBottomViewport && (
                        <div className="mobile-bottom-switch" role="tablist" aria-label="Bottom panel switcher">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={mobileBottomPanel === 'sidebar'}
                                className={`mobile-bottom-switch__btn ${mobileBottomPanel === 'sidebar' ? 'active' : ''}`}
                                onClick={() => setMobileBottomPanel('sidebar')}
                            >
                                {mobileSidebarTabLabel}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={mobileBottomPanel === 'popout'}
                                className={`mobile-bottom-switch__btn ${mobileBottomPanel === 'popout' ? 'active' : ''}`}
                                onClick={() => setMobileBottomPanel('popout')}
                            >
                                {mobilePopoutTabLabel}
                            </button>
                        </div>
                    )}
                    <div className="mobile-bottom-panel__content">
                        {isPhoneBottomViewport && mobileBottomPanel === 'popout' && getDefaultPopoutTabLabelFromPath(pathname) === 'Production' ? (
                            <MobileProductionPanel/>
                        ) : (
                            <Sidebar/>
                        )}
                    </div>
                </div>
            )}

            {!isViewportOnlyEmbed ? (
                <div className='app__controls'>
                    <Compass/>
                    <Navigation/>
                </div>
            ) : null}
        </div>
    );
}

export default EditorPage;
